// @flow
import { get, first, reject } from 'lodash';
import moment from 'moment';
import { isPresent } from './validations';
import type { ContextState, ReportPeriodFilter } from '@types';
/**
 * Check if current user is a secretary from state
 * @param {*} state
 */
export const isSecretary = (state: Object) => get(state, ['context', 'user', 'roleType']) === 'Secretary';


export const getServicesMenuLinkId = (isDoctor: boolean, doctorId?: ?number, doctorIds: number[]) => {
  if (isDoctor && doctorId) {
    return doctorId;
  } else if (!isDoctor && !doctorId) {
    return first(doctorIds);
  }
  return String(doctorId);
};


export const createCachedAction = (
  loadingAction: string,
  loadedAction: string,
  errorAction: string,
  durationHours: number,
  executeRequest: Function,
  ttlPath: string[],
  parseResponse: Function) =>
  (ignoreCache: boolean, payload: any = {}) => ({
    types: [loadingAction, loadedAction, errorAction],
    payload,
    shouldCall: (state: ContextState) => {
      const ttl = get(state, ttlPath, undefined);
      return (ttl && (parseInt(moment.duration(moment().diff(moment(ttl, 'YYYY-MM-DD HH:mm:ss'))).asHours(), 10) > 0)) || ignoreCache || !ttl;
    },
    call: executeRequest,
    parseResponse: (response: any) => ({
      ...parseResponse(response),
      ttl: moment().add(durationHours, 'hours').set('seconds', 0).format('YYYY-MM-DD HH:mm:ss'),
    }),
  });


// The ids a list deleted in this session, for its delete action to note and its list request to leave out
// (createCachedAction's parseResponse). A list answer the API sent before a delete went through still has the
// deleted item, and landing after the delete it put the item back on screen, where deleting it again was a 404
// (Bugsnag 6ae0ebef…, the templates of Receituário Eletrônico). Ids are never reused, so leaving them out never
// hides an item that still exists.
export const createDeletedIds = () => {
  const ids: Set<string> = new Set();
  return {
    add: (id: any) => { ids.add(String(id)); },
    without: (items: any) => reject(items, (item) => ids.has(String(get(item, 'id')))),
  };
};

export const handleReportPeriods = (startDate: string, endDate: string, period: string, month: number, year: number) => {
  const DT_FORMAT = 'YYYY-MM-DD HH:mm:[00]';

  const ADD_YEARS = 100;


  if (period === 'future') {
    return {
      startDate: moment().startOf('day').startOf('day').format(DT_FORMAT),
      endDate: moment().add(ADD_YEARS, 'years').endOf('day').format(DT_FORMAT),
    };
  } else if (period === 'today') {
    return {
      startDate: moment().startOf('day').format(DT_FORMAT),
      endDate: moment().endOf('day').format(DT_FORMAT),
    };
  } else if (period === 'yesterday') {
    return {
      startDate: moment().subtract(1, 'day').startOf('day').format(DT_FORMAT),
      endDate: moment().subtract(1, 'day').endOf('day').format(DT_FORMAT),
    };
  } else if (period === 'this_month') {
    return {
      startDate: moment().startOf('month').startOf('day').format(DT_FORMAT),
      endDate: moment().endOf('month').endOf('day').format(DT_FORMAT),
    };
  } else if (period === 'last_month') {
    return {
      startDate: moment().subtract(1, 'month').startOf('month').startOf('day')
        .format(DT_FORMAT),
      endDate: moment().subtract(1, 'month').endOf('month').endOf('day')
        .format(DT_FORMAT),
    };
  } else if (period === 'custom_month') {
    const dt = moment().set('month', month - 1).set('year', year).set('hour', 12);
    return {
      startDate: dt.clone().startOf('month').startOf('day').format(DT_FORMAT),
      endDate: dt.clone().endOf('month').endOf('day').format(DT_FORMAT),
    };
  } else if (period === 'custom_year') {
    const dt = moment().set('date', 1).set('year', year).set('hour', 12);
    return {
      startDate: dt.clone().startOf('year').startOf('day').format(DT_FORMAT),
      endDate: dt.clone().endOf('year').endOf('day').format(DT_FORMAT),
    };
  } else {
    return {
      startDate: moment(startDate).startOf('day').format(DT_FORMAT),
      endDate: moment(endDate).endOf('day').format(DT_FORMAT),
    };
  }
};

export const isReportDownloadByEmail = (stateFn: Function) => get(stateFn(), ['context', 'clinics', 'activeClinic', 'sendReportsViaEmail']);

// custom_month / custom_year xlsx reports are always delivered by email:
// the backend answers 204 No Content instead of a file body
export const isReportEmailOnlyPeriod = (period: ?string) => period === 'custom_month' || period === 'custom_year';

export const reportPeriodFilters = (periodFilter: ?ReportPeriodFilter) => {
  const period = get(periodFilter, 'period');
  if (!isPresent(period)) {
    return {};
  }
  const filters: { [key: string]: any } = { 'filter[period]': period };
  if (period === 'custom_month') {
    filters['filter[month]'] = get(periodFilter, 'month');
    filters['filter[year]'] = get(periodFilter, 'year');
  } else if (period === 'custom_year') {
    filters['filter[year]'] = get(periodFilter, 'year');
  }
  return filters;
};

